<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { ReportColumn } from '../../models/column.types'
import {
  MATCH_MODES,
  type FilterDropdownables,
  type FilterMatchMode,
  type ReportFilter,
  type SavedReportFilterInput,
} from '../../models/filter.types'
import {
  isNumericReportColumn,
  isReportFilterDropdownableColumn,
  reportFilterSubTypeForColumn,
} from '../../service/reportColumnKind'
import { toReportColumnOption } from '../../service/reportColumnOption'
import {
  cloneReportFilter,
  emptyReportFilter,
  initialReportFilterValue,
  isValidReportFilterInput,
  normalizeReportFilterInput,
} from '../../service/reportFilterInput'
import {
  getAvailableReportFilterModes,
  getDefaultReportFilterMode,
  getReportFilterModeDefinition,
} from '../../service/reportFilterModes'

import FilterValueEditor from './FilterValueEditor.vue'

import { useI18n } from '@/shared/i18n'
import { Button, Checkbox, Input, Select, type SelectOption } from '@/shared/ui'

const props = withDefaults(
  defineProps<{
    columns: readonly ReportColumn[]
    modelValue?: ReportFilter | undefined
    column?: ReportColumn | undefined
    dropdownables?: FilterDropdownables
    matchModes?: readonly FilterMatchMode[]
    numericMode?: FilterMatchMode
    checkChange?: boolean
    isExport?: boolean
    savedId?: string | undefined
    savedName?: string | undefined
    onLoadDropdownValues?: ((field: string) => Promise<unknown> | unknown) | undefined
  }>(),
  {
    dropdownables: () => ({}),
    matchModes: () => MATCH_MODES,
    numericMode: 'greater_than',
    checkChange: false,
    isExport: false,
    savedName: '',
  },
)
const emit = defineEmits<{
  apply: [filter: ReportFilter]
  save: [saved: SavedReportFilterInput]
  remove: [id: string]
  close: []
}>()

const { t } = useI18n()
const formClass =
  'filter-popup w-[min(40rem,calc(100vw-2rem))] rounded-md border border-border bg-surface p-md shadow-pop max-md:w-[min(24rem,calc(100vw-2rem))]'
const draft = ref<ReportFilter>(emptyReportFilter())
const saveOpen = ref(Boolean(props.savedName))
const filterName = ref(props.savedName)
const attempted = ref(false)
const loadingField = ref<string | null>(null)
const failedField = ref<string | null>(null)
const probedFields = new Set<string>()

const visibleColumns = computed(() =>
  props.columns.filter(
    (column) =>
      !column.invisible &&
      !column.isImage &&
      (!draft.value.is_change || isNumericReportColumn(column)),
  ),
)
const columnOptions = computed<SelectOption<string>[]>(() =>
  visibleColumns.value.map((column) => toReportColumnOption(column)),
)
const selectedColumn = computed(
  () => props.columns.find((column) => column.field === draft.value.field) ?? null,
)
const selectedField = computed<string | null>({
  get: () => draft.value.field || null,
  set: (field) => field && setField(field),
})
const allowedModes = computed<readonly FilterMatchMode[]>(() => {
  const criteria = filterCriteriaForColumn(selectedColumn.value)
  if (!props.isExport || !Array.isArray(criteria)) return props.matchModes
  const allowed = new Set(criteria.filter(isFilterMatchMode))
  return props.matchModes.filter((mode) => allowed.has(mode))
})
const availableModes = computed(() =>
  selectedColumn.value
    ? getAvailableReportFilterModes(selectedColumn.value, allowedModes.value)
    : [],
)
const modeOptions = computed<SelectOption<FilterMatchMode>[]>(() =>
  availableModes.value.map((value) => ({
    value,
    label: t(getReportFilterModeDefinition(value).labelKey),
  })),
)
const selectedMode = computed<FilterMatchMode | null>({
  get: () => draft.value.match_mode || null,
  set: (mode) => mode && setMode(mode),
})
const currentDropdownable = computed(() => props.dropdownables[draft.value.field])
const dropdownState = computed(() => {
  if (loadingField.value === draft.value.field) return 'loading' as const
  if (failedField.value === draft.value.field) return 'error' as const
  return 'ready' as const
})
const changeEligible = computed(() =>
  Boolean(
    props.checkChange &&
    !props.isExport &&
    selectedColumn.value &&
    isNumericReportColumn(selectedColumn.value),
  ),
)
const isChange = computed({
  get: () => Boolean(draft.value.is_change),
  set: (value) => {
    const { direction: _direction, ...current } = draft.value
    draft.value = {
      ...current,
      is_change: value,
      ...(value ? { direction: draft.value.direction ?? 'increase' } : {}),
    }
  },
})
const direction = computed<'increase' | 'decrease' | null>({
  get: () => draft.value.direction ?? null,
  set: (value) => {
    const { direction: _direction, ...current } = draft.value
    draft.value = { ...current, ...(value ? { direction: value } : {}) }
  },
})
const directionOptions: SelectOption<'increase' | 'decrease'>[] = [
  { value: 'increase', label: t('reports.filters.increase') },
  { value: 'decrease', label: t('reports.filters.decrease') },
]
const nameError = computed(() => attempted.value && saveOpen.value && !filterName.value.trim())
const valueError = computed(
  () => attempted.value && !isValidReportFilterInput(draft.value, props.columns),
)
const valid = computed(
  () =>
    Boolean(selectedColumn.value && availableModes.value.includes(draft.value.match_mode)) &&
    (!draft.value.is_change || Boolean(draft.value.direction)),
)

watch(
  () => [props.modelValue, props.column] as const,
  ([value, column]) => hydrate(value, column),
  { immediate: true, deep: true },
)
watch(
  () =>
    [
      draft.value.field,
      draft.value.match_mode,
      currentDropdownable.value?.unique_count,
      currentDropdownable.value?.values,
      props.onLoadDropdownValues,
    ] as const,
  () => void loadValuesIfNeeded(),
  { immediate: true },
)

function hydrate(value?: ReportFilter, column?: ReportColumn): void {
  filterName.value = props.savedName
  saveOpen.value = Boolean(props.savedName)
  attempted.value = false
  if (value) {
    draft.value = cloneReportFilter(value)
    return
  }
  if (column) setField(column.field)
  else draft.value = emptyReportFilter()
}

function isFilterMatchMode(value: unknown): value is FilterMatchMode {
  return typeof value === 'string' && (MATCH_MODES as readonly string[]).includes(value)
}

function setField(field: string): void {
  const column = props.columns.find((item) => item.field === field)
  if (!column) return
  const allowed = allowedModesFor(column)
  const available = getAvailableReportFilterModes(column, allowed)
  const confirmedDropdown = Boolean(
    isReportFilterDropdownableColumn(column) &&
    props.dropdownables[field]?.unique_count &&
    available.includes('in'),
  )
  const preferred = confirmedDropdown
    ? 'in'
    : isNumericReportColumn(column) && available.includes(props.numericMode)
      ? props.numericMode
      : getDefaultReportFilterMode(column, allowed)
  const matchMode = preferred ?? available[0] ?? 'contains'
  const subType = reportFilterSubTypeForColumn(column)
  const preserveChange = Boolean(
    draft.value.is_change && !props.isExport && isNumericReportColumn(column),
  )
  draft.value = {
    field,
    match_mode: matchMode,
    value: initialReportFilterValue(matchMode),
    ...(subType === undefined ? {} : { sub_type: subType }),
    ...(preserveChange ? { is_change: true, direction: draft.value.direction ?? 'increase' } : {}),
  }
}

function allowedModesFor(column: ReportColumn): readonly FilterMatchMode[] {
  const criteria = filterCriteriaForColumn(column)
  if (!props.isExport || !Array.isArray(criteria)) return props.matchModes
  const allowed = new Set(criteria.filter(isFilterMatchMode))
  return props.matchModes.filter((mode) => allowed.has(mode))
}

function filterCriteriaForColumn(column: ReportColumn | null | undefined): unknown {
  return column?.filter_criteria ?? column?.meta?.filter_criteria
}

function setMode(mode: FilterMatchMode): void {
  draft.value = { ...draft.value, match_mode: mode, value: initialReportFilterValue(mode) }
}

async function loadValuesIfNeeded(): Promise<void> {
  const dropdownable = currentDropdownable.value
  const field = draft.value.field
  const column = selectedColumn.value
  const coldProbe = dropdownable === undefined
  const modeNeedsValues = ['equals', 'not_equals', 'in', 'not_in'].includes(draft.value.match_mode)
  if (
    !field ||
    !column ||
    !isReportFilterDropdownableColumn(column) ||
    dropdownable?.unique_count === 0 ||
    dropdownable?.values !== undefined ||
    (!coldProbe && !modeNeedsValues) ||
    !props.onLoadDropdownValues ||
    loadingField.value === field ||
    (coldProbe && probedFields.has(field))
  )
    return
  if (coldProbe) probedFields.add(field)
  const modeAtRequest = draft.value.match_mode
  const valueAtRequest = Array.isArray(draft.value.value)
    ? [...draft.value.value]
    : draft.value.value
  const pristineAtRequest =
    valueAtRequest === null ||
    valueAtRequest === undefined ||
    valueAtRequest === '' ||
    (Array.isArray(valueAtRequest) && valueAtRequest.length === 0)
  loadingField.value = field
  failedField.value = null
  try {
    const result = await props.onLoadDropdownValues(field)
    const loadedValues = currentDropdownable.value?.values ?? (Array.isArray(result) ? result : [])
    if (
      coldProbe &&
      draft.value.field === field &&
      draft.value.match_mode === modeAtRequest &&
      pristineAtRequest &&
      JSON.stringify(draft.value.value) === JSON.stringify(valueAtRequest) &&
      loadedValues.length > 0 &&
      availableModes.value.includes('in')
    ) {
      setMode('in')
    }
  } catch {
    failedField.value = field
  } finally {
    if (loadingField.value === field) loadingField.value = null
  }
}

function apply(): void {
  attempted.value = true
  if (
    !valid.value ||
    !isValidReportFilterInput(draft.value, props.columns) ||
    (saveOpen.value && !filterName.value.trim())
  )
    return
  const filter = normalizeReportFilterInput(draft.value, props.columns)
  if (saveOpen.value) {
    emit('save', {
      ...(props.savedId ? { id: props.savedId } : {}),
      name: filterName.value.trim(),
      filter,
    })
  }
  emit('apply', filter)
}
</script>

<template>
  <form :class="formClass" :aria-label="t('reports.filters.title')" @submit.prevent="apply">
    <!-- Widths sit on wrappers: `Select`'s own root is `w-full`, which beat a width class passed to
         it and let the column picker swallow the row, pushing operator and value out of the popup. -->
    <div class="filter-popup__fields flex items-start gap-sm max-md:flex-col max-md:items-stretch">
      <div class="filter-popup__field-column w-36 shrink-0 max-md:w-full">
        <Select
          v-model="selectedField"
          :options="columnOptions"
          :label="t('reports.filters.column')"
          :placeholder="t('reports.filters.selectField')"
          searchable
          size="sm"
        />
      </div>
      <div class="filter-popup__field-operator w-36 shrink-0 max-md:w-full">
        <Select
          v-model="selectedMode"
          :options="modeOptions"
          :label="t('reports.filters.operator')"
          :placeholder="t('reports.filters.selectCondition')"
          size="sm"
        />
      </div>
      <FilterValueEditor
        v-if="selectedColumn && selectedMode"
        v-model="draft"
        class="filter-popup__field-value min-w-0 flex-[1_1_15rem] max-md:w-full max-md:flex-none"
        :column="selectedColumn"
        :dropdownable="currentDropdownable"
        :dropdown-enabled="Boolean(onLoadDropdownValues)"
        :state="dropdownState"
      />
      <div v-if="draft.is_change" class="filter-popup__field-direction w-36 shrink-0 max-md:w-full">
        <Select
          v-model="direction"
          :options="directionOptions"
          :label="t('reports.filters.direction')"
          :placeholder="t('reports.filters.direction')"
          size="sm"
        />
      </div>
    </div>
    <p v-if="valueError" class="filter-popup__error m-0 mt-xs text-[0.75rem] text-danger">
      {{ t('reports.filters.valueRequired') }}
    </p>
    <div class="filter-popup__actions flex items-center justify-end gap-sm mt-md max-md:flex-wrap">
      <button
        v-if="!saveOpen"
        type="button"
        class="filter-popup__save-link mr-auto cursor-pointer border-0 bg-transparent text-[0.75rem] font-semibold text-fg hover:underline"
        @click="saveOpen = true"
      >
        {{ t('reports.filters.saveFilter') }}
      </button>
      <Input
        v-else
        v-model="filterName"
        class="filter-popup__name w-[10rem] mr-auto"
        :label="t('reports.filters.filterName')"
        :placeholder="t('reports.filters.filterNamePlaceholder')"
        :error="nameError"
        v-bind="nameError ? { errorMessage: t('reports.filters.savedFilterNameRequired') } : {}"
        size="sm"
      />
      <Checkbox
        v-if="changeEligible"
        v-model="isChange"
        :label="t('reports.filters.changePercentage')"
      />
      <Button
        v-if="savedId"
        type="button"
        variant="ghost"
        size="sm"
        @click="emit('remove', savedId)"
      >
        {{ t('reports.filters.deleteSavedFilter') }}
      </Button>
      <Button type="button" variant="data" size="sm" @click="emit('close')">
        {{ t('reports.actions.close') }}
      </Button>
      <Button type="submit" size="sm" :disabled="!valid">
        {{ savedId && saveOpen ? t('reports.filters.update') : t('reports.actions.apply') }}
      </Button>
    </div>
  </form>
</template>

<style scoped src="./FilterPopup.css"></style>
